import {ActivityIndicator, StyleSheet} from 'react-native';
import {Box, HBox} from '../../components/core/view';
import {CustomText, H3Text, H6Text} from '../../components/core/text';
import React, {useMemo} from 'react';
import {useColorThemeType, useFontTheme} from '../../appStyles/hooks/theme';

import BottomSheet from '../../components/composites/bottomSheet';
import {OutreachShift} from '../types/shiftClaim';
import {Touchable} from '@buncha/components/core/touchable';
import font from '@buncha/appStyles/style/font';
import {formatShiftTime, parseStoreName} from '../utils/formatShiftTime';

interface ShiftClaimActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  actionType: 'release' | 'pass' | null;
  shift: OutreachShift | null;
  isLoading: boolean;
  onConfirm: () => void;
}

const ShiftClaimActionSheet: React.FC<ShiftClaimActionSheetProps> = ({
  isOpen,
  onClose,
  actionType,
  shift,
  isLoading,
  onConfirm,
}) => {
  const storeName = shift ? parseStoreName(shift.store_name) : '';
  const theme = useColorThemeType('main');
  const mortise = useFontTheme('mortise');

  const shiftDate = shift?.shopper_shift_start
    ? new Date(shift.shopper_shift_start).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
    : '';

  const shiftTime =
    shift?.shopper_shift_start && shift?.driver_shift_end
      ? `${formatShiftTime(shift.shopper_shift_start)} – ${formatShiftTime(
          shift.driver_shift_end,
        )}`
      : '';

  const isRelease = actionType === 'release';

  const title = isRelease ? 'Release this shift?' : 'Pass on this shift?';

  const description = useMemo(() => {
    if (isRelease)
      return (
        <CustomText style={[styles.descText, {color: theme?.text.gray[100]}]}>
          It goes back to the pool for another shopper.
        </CustomText>
      );

    return (
      <CustomText style={[styles.descText, {color: theme?.text.gray[100]}]}>
        <CustomText
          style={[
            {
              color: theme?.text.blue[300],
              fontFamily: font.fontTheme.muli[700].normal,
            },
          ]}>
          {shiftDate} • {shiftTime}
        </CustomText>{' '}
        at {storeName}. We won't offer it to you again today.
      </CustomText>
    );
  }, [
    isRelease,
    shiftDate,
    shiftTime,
    storeName,
    theme?.text.blue,
    theme?.text.gray,
  ]);

  const confirmText = isRelease ? 'Release' : 'Yes, pass';
  const confirmBgColor = isRelease
    ? theme?.background.blue[300]
    : theme?.background.red[100];

  return (
    <BottomSheet open={isOpen} onClose={onClose}>
      <Box style={styles.container}>
        <H3Text
          style={[
            styles.title,
            {fontFamily: mortise[700].normal, color: theme?.text.blue[300]},
          ]}>
          {title}
        </H3Text>

        {description}

        <HBox style={styles.actions}>
          <Touchable
            style={[styles.btnOutline, {borderColor: theme?.text.gray[100]}]}
            onPress={onClose}
            disabled={isLoading}
            activeOpacity={0.7}>
            <H6Text
              style={[styles.btnOutlineText, {color: theme?.text.blue[300]}]}>
              Keep it
            </H6Text>
          </Touchable>

          <Touchable
            style={[styles.btnSolid, {backgroundColor: confirmBgColor}]}
            onPress={onConfirm}
            disabled={isLoading}
            loading={isLoading}
            loadingSkeleton={
              <ActivityIndicator color={theme?.text.white[100]} size="small" />
            }
            activeOpacity={0.8}>
            <H6Text style={[{color: theme?.text.white[100]}]}>
              {confirmText}
            </H6Text>
          </Touchable>
        </HBox>
      </Box>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 10,
    paddingTop: 8,
    paddingBottom: 24,
    alignItems: 'flex-start',
    width: '100%',
  },
  dragHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    marginBottom: 20,
  },
  title: {
    marginBottom: 8,
    textAlign: 'center',
  },
  descText: {
    textAlign: 'left',
    lineHeight: 20,
    marginBottom: 20,
    alignSelf: 'stretch',
  },
  actions: {
    justifyContent: 'space-between',
    gap: 12,
    width: '100%',
  },
  btnOutline: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnOutlineText: {
    fontWeight: '600',
  },
  btnSolid: {
    flex: 1,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default React.memo(ShiftClaimActionSheet);
